import { useState } from 'react'
import {
  useFeedbackList,
  useFeedbackNewCount,
  useFeedbackDetail,
  useUpdateFeedbackStatus,
} from '@/hooks/useFeedback'
import { DataTable, type ColumnDef } from '@/components/common/DataTable'
import { SearchInput } from '@/components/common/SearchInput'
import { FilterDropdown } from '@/components/common/FilterDropdown'
import { StatusBadge } from '@/components/common/StatusBadge'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Feedback, FeedbackFilter, FeedbackUpdateStatusPayload } from '@/types'
import { formatRelativeTime } from '@/lib/utils'
import { Eye, Bug, Lightbulb, HelpCircle, MessageSquare } from 'lucide-react'

const TYPE_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'bug', label: 'Bug' },
  { value: 'suggestion', label: 'Sugerencia' },
  { value: 'question', label: 'Pregunta' },
  { value: 'other', label: 'Otro' },
]

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'new', label: 'Nuevo' },
  { value: 'reviewed', label: 'Revisado' },
  { value: 'resolved', label: 'Resuelto' },
]

const TYPE_ICONS: Record<string, typeof Bug> = {
  bug: Bug,
  suggestion: Lightbulb,
  question: HelpCircle,
  other: MessageSquare,
}

const TYPE_LABELS: Record<string, string> = {
  bug: 'Bug',
  suggestion: 'Sugerencia',
  question: 'Pregunta',
  other: 'Otro',
}

const TYPE_COLORS: Record<string, string> = {
  bug: 'text-red-500',
  suggestion: 'text-yellow-500',
  question: 'text-blue-500',
  other: 'text-gray-500',
}

export const FeedbackPage = () => {
  const [filters, setFilters] = useState<FeedbackFilter>({ page: 1, limit: 20 })
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')

  // Dialogs
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showDetail, setShowDetail] = useState(false)
  const [showUpdate, setShowUpdate] = useState(false)

  // Update form
  const [updateForm, setUpdateForm] = useState<FeedbackUpdateStatusPayload>({
    status: 'reviewed',
    admin_notes: '',
  })

  const currentFilters: FeedbackFilter = {
    ...filters,
    search: search || undefined,
    type: typeFilter !== 'all' ? typeFilter : undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
  }

  const { data, isLoading } = useFeedbackList(currentFilters)
  const { data: newCount } = useFeedbackNewCount()
  const { data: selectedFeedback } = useFeedbackDetail(selectedId || '')
  const updateMutation = useUpdateFeedbackStatus()

  const handleUpdate = () => {
    if (!selectedId) return
    updateMutation.mutate(
      { id: selectedId, payload: updateForm },
      {
        onSuccess: () => {
          setShowUpdate(false)
          setSelectedId(null)
        },
      }
    )
  }

  const openDetail = (feedback: Feedback) => {
    setSelectedId(feedback.id)
    setShowDetail(true)
  }

  const openUpdate = (feedback: Feedback) => {
    setSelectedId(feedback.id)
    setUpdateForm({
      status: feedback.status === 'new' ? 'reviewed' : 'resolved',
      admin_notes: feedback.admin_notes || '',
    })
    setShowUpdate(true)
  }

  const columns: ColumnDef<Feedback>[] = [
    {
      key: 'type',
      header: 'Tipo',
      cell: (row: Feedback) => {
        const Icon = TYPE_ICONS[row.type] || MessageSquare
        return (
          <div className="flex items-center gap-2">
            <Icon className={`h-4 w-4 ${TYPE_COLORS[row.type] || ''}`} />
            <span>{TYPE_LABELS[row.type] || row.type}</span>
          </div>
        )
      },
    },
    {
      key: 'user',
      header: 'Usuario',
      cell: (row: Feedback) => (
        <div>
          <p className="font-medium">{row.user_name}</p>
          <p className="text-xs text-muted-foreground">{row.user_email}</p>
        </div>
      ),
    },
    {
      key: 'message',
      header: 'Mensaje',
      cell: (row: Feedback) => (
        <p className="max-w-xs truncate" title={row.message}>
          {row.message.length > 80 ? `${row.message.slice(0, 80)}...` : row.message}
        </p>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row: Feedback) => <StatusBadge status={row.status} />,
    },
    {
      key: 'created_at',
      header: 'Fecha',
      cell: (row: Feedback) => formatRelativeTime(row.created_at),
    },
    {
      key: 'actions',
      header: 'Acciones',
      cell: (row: Feedback) => (
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={() => openDetail(row)}>
            <Eye className="h-4 w-4" />
          </Button>
          {row.status !== 'resolved' && (
            <Button size="sm" variant="outline" onClick={() => openUpdate(row)} className="text-xs">
              {row.status === 'new' ? 'Revisar' : 'Resolver'}
            </Button>
          )}
        </div>
      ),
    },
  ]

  const newBadge = newCount?.count || 0

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Feedback</h1>
        <p className="text-muted-foreground">
          Gestiona el feedback de usuarios: bugs, sugerencias y preguntas
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Buscar por mensaje, email, nombre..."
        />
        <FilterDropdown
          label="Tipo"
          options={TYPE_OPTIONS}
          value={typeFilter}
          onChange={setTypeFilter}
        />
        <FilterDropdown
          label="Status"
          options={STATUS_OPTIONS}
          value={statusFilter}
          onChange={setStatusFilter}
        />
        {newBadge > 0 && (
          <Badge variant="error" className="h-6 px-2">
            {newBadge} nuevos
          </Badge>
        )}
      </div>

      <DataTable
        columns={columns}
        data={data?.items || []}
        isLoading={isLoading}
        keyExtractor={(r) => r.id}
        pagination={{
          page: data?.page || 1,
          pages: data?.pages || 1,
          onPageChange: (page) => setFilters((f) => ({ ...f, page })),
        }}
      />

      {/* Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalle de Feedback</DialogTitle>
          </DialogHeader>
          {selectedFeedback && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-muted-foreground">Tipo</p>
                  <div className="flex items-center gap-2 font-medium">
                    {(() => {
                      const Icon = TYPE_ICONS[selectedFeedback.type] || MessageSquare
                      return (
                        <Icon className={`h-4 w-4 ${TYPE_COLORS[selectedFeedback.type] || ''}`} />
                      )
                    })()}
                    {TYPE_LABELS[selectedFeedback.type] || selectedFeedback.type}
                  </div>
                </div>
                <div>
                  <p className="text-muted-foreground">Status</p>
                  <StatusBadge status={selectedFeedback.status} />
                </div>
                <div>
                  <p className="text-muted-foreground">Usuario</p>
                  <p className="font-medium">{selectedFeedback.user_name}</p>
                  <p className="text-xs text-muted-foreground">{selectedFeedback.user_email}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Fecha</p>
                  <p className="font-medium">{formatRelativeTime(selectedFeedback.created_at)}</p>
                </div>
              </div>

              <div>
                <p className="mb-1 text-muted-foreground">Mensaje</p>
                <div className="whitespace-pre-wrap rounded-lg bg-muted p-3">
                  {selectedFeedback.message}
                </div>
              </div>

              {selectedFeedback.image_url && (
                <div>
                  <p className="mb-1 text-muted-foreground">Imagen adjunta</p>
                  <img
                    src={selectedFeedback.image_url}
                    alt="Feedback screenshot"
                    className="max-h-64 rounded-lg border"
                  />
                </div>
              )}

              {selectedFeedback.page_url && (
                <div>
                  <p className="text-muted-foreground">Pagina</p>
                  <p className="break-all text-xs font-medium">{selectedFeedback.page_url}</p>
                </div>
              )}

              {selectedFeedback.user_agent && (
                <div>
                  <p className="text-muted-foreground">User Agent</p>
                  <p className="break-all text-xs font-medium">{selectedFeedback.user_agent}</p>
                </div>
              )}

              {selectedFeedback.admin_notes && (
                <div>
                  <p className="text-muted-foreground">Notas del admin</p>
                  <div className="rounded-lg bg-blue-50 p-3 text-blue-900">
                    {selectedFeedback.admin_notes}
                  </div>
                </div>
              )}

              {selectedFeedback.reviewed_by && (
                <div>
                  <p className="text-muted-foreground">Revisado por</p>
                  <p className="font-medium">
                    {selectedFeedback.reviewed_by}{' '}
                    {selectedFeedback.reviewed_at
                      ? `(${formatRelativeTime(selectedFeedback.reviewed_at)})`
                      : ''}
                  </p>
                </div>
              )}

              {selectedFeedback.resolved_by && (
                <div>
                  <p className="text-muted-foreground">Resuelto por</p>
                  <p className="font-medium">
                    {selectedFeedback.resolved_by}{' '}
                    {selectedFeedback.resolved_at
                      ? `(${formatRelativeTime(selectedFeedback.resolved_at)})`
                      : ''}
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {selectedFeedback && selectedFeedback.status !== 'resolved' && (
              <Button
                onClick={() => {
                  setShowDetail(false)
                  if (selectedFeedback) openUpdate(selectedFeedback)
                }}
              >
                {selectedFeedback.status === 'new'
                  ? 'Marcar como revisado'
                  : 'Marcar como resuelto'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={showUpdate} onOpenChange={setShowUpdate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Actualizar Feedback</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={updateForm.status}
                onValueChange={(v) =>
                  setUpdateForm((f) => ({
                    ...f,
                    status: v as FeedbackUpdateStatusPayload['status'],
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">Nuevo</SelectItem>
                  <SelectItem value="reviewed">Revisado</SelectItem>
                  <SelectItem value="resolved">Resuelto</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notas del admin (opcional)</Label>
              <Textarea
                value={updateForm.admin_notes || ''}
                onChange={(e) => setUpdateForm((f) => ({ ...f, admin_notes: e.target.value }))}
                placeholder="Agrega notas internas sobre este feedback..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpdate(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
